import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';
import styles from './FAQPage.module.css';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  items: FAQItem[];
}

const faqData: FAQCategory[] = [
  {
    title: 'Login & Account',
    items: [
      {
        question: 'How do I sign in to my account?',
        answer: 'Enter your email address on the login page and click "Send Login Code". We\'ll send you a one-time verification code to your email. Enter that code to access your account.'
      },
      {
        question: 'I didn\'t receive my verification code. What should I do?',
        answer: 'Check your spam or junk folder first. If you still don\'t see it, wait a few minutes and try requesting a new code. Make sure you entered the correct email address.'
      },
      {
        question: 'How do I reset my password?',
        answer: 'Peapil Health uses passwordless authentication. Simply enter your email on the login page to receive a new verification code each time you sign in. No password is required.'
      },
      {
        question: 'How do I create a new account?',
        answer: 'Enter your email address on the login page. If you\'re a new user, we\'ll automatically create an account for you when you verify your email.'
      },
      {
        question: 'What is a One-Time Passcode?',
        answer: 'A One-Time Passcode is a temporary code we send to your email to help you log in securely. Instead of remembering a password: 1) You enter your email address, 2) We send you a short code, 3) You type that code into the app, 4) You\'re signed in. Each code can only be used once.'
      },
      {
        question: 'Is a One-Time Passcode safe?',
        answer: 'Yes. One-Time Passcodes are very secure and commonly used by banks and healthcare apps. The code is sent only to your email, it expires after a short time, and it cannot be reused. If you didn\'t request a code, you can safely ignore the email.'
      },
      {
        question: 'Do I have to log in every time I use the app?',
        answer: 'No. In most cases, you\'ll stay logged in. If you are logged out, simply request a new One-Time Passcode, enter the code, and you\'re back in.'
      }
    ]
  },
  {
    title: 'Using Peapil Health',
    items: [
      {
        question: 'What does "Update the app" mean?',
        answer: 'Updating the app means installing the latest version. Updates fix problems, improve performance, and make things easier to use. Updates do not erase your information.'
      },
      {
        question: 'Why am I being asked to update the app?',
        answer: 'You may be asked to update when a new feature is added, a problem has been fixed, or your current version is very old. Keeping the app updated helps everything work smoothly.'
      },
      {
        question: 'How do I update the app on an iPhone?',
        answer: '1) Open the App Store, 2) Tap your profile picture in the top-right corner, 3) Scroll to find the app, 4) Tap Update. If you don\'t see an Update button, your app is already up to date.'
      },
      {
        question: 'How do I update the app on an Android phone?',
        answer: '1) Open the Google Play Store, 2) Tap your profile icon, 3) Tap Manage apps & device, 4) Tap Update next to the app.'
      },
      {
        question: 'Will updating the app delete my information?',
        answer: 'No. Updating the app will not delete your account, your settings, or your saved information. Everything stays the same — just improved.'
      },
      {
        question: 'I tapped something and now I\'m on a different screen. What do I do?',
        answer: 'This happens to everyone. You can tap the Back button, close the app and reopen it, or return to the main screen. You cannot permanently break anything by tapping.'
      },
      {
        question: 'What if the app freezes or doesn\'t respond?',
        answer: 'Try these steps: 1) Close the app completely, 2) Reopen it, 3) Make sure the app is updated. If the issue continues, contact support and we\'ll help you.'
      },
      {
        question: 'Is my personal information safe?',
        answer: 'Yes. Your privacy is very important to us. Your data is encrypted and only you can access your account.'
      },
      {
        question: 'I forgot what to do next in the app. Is that normal?',
        answer: 'Yes — this is very common. The app is designed so you can take your time, go back if needed, and review instructions at any time. There is no rush and no penalty for going slowly.'
      },
      {
        question: 'Can someone else use my account?',
        answer: 'No — unless they have access to your email. Each One-Time Passcode is sent directly to your email address. If you need help, it\'s okay to ask someone you trust to assist you.'
      }
    ]
  },
  {
    title: 'Billing & Subscription',
    items: [
      {
        question: 'What subscription plans are available?',
        answer: 'We offer several plans to fit your needs. Visit the Profile section in the app or contact our support team for current pricing and plan details.'
      },
      {
        question: 'Can I cancel my subscription?',
        answer: 'Yes, you can cancel your subscription at any time from your Profile page. Your access will continue until the end of your current billing period.'
      },
      {
        question: 'Will my data be saved if I cancel?',
        answer: 'Your data remains saved for 90 days after cancellation. If you resubscribe within that time, all your data will be restored.'
      },
      {
        question: 'I have a subscription but my access is blocked or shows "Free" in my Profile.',
        answer: 'This usually happens when you\'re signed in with a different email than the one linked to your subscription. Please make sure you\'re logged in with the same email address you used when subscribing. If you\'re still having trouble, contact our support team and we\'ll be happy to look into it for you.'
      }
    ]
  },
  {
    title: 'Support',
    items: [
      {
        question: 'How do I contact customer support?',
        answer: 'You can reach our support team by emailing <a href="mailto:support@peapilhealth.com">support@peapilhealth.com</a>. We\'re here to help with any questions or issues you may have.'
      },
      {
        question: 'How do I report a bug or technical issue?',
        answer: 'Please email <a href="mailto:support@peapilhealth.com?subject=Bug%20Report">support@peapilhealth.com</a> with a detailed description of the issue, including what you were doing when it occurred and any error messages you saw.'
      },
      {
        question: 'How can I request a new feature?',
        answer: 'We love hearing from our users! Send your feature suggestions to <a href="mailto:support@peapilhealth.com?subject=Feature%20Request">support@peapilhealth.com</a> with the subject line "Feature Request".'
      },
      {
        question: 'Is there a mobile app available?',
        answer: 'Yes! Peapil Health is available as a mobile app for iOS and Android. Visit <a href="https://get.peapilhealth.com" target="_blank" rel="noopener noreferrer">get.peapilhealth.com</a> to download the app for your device.'
      }
    ]
  }
];

function FAQItemComponent({ item }: { item: FAQItem }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={styles.faqItem}>
      <button
        className={styles.question}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span>{item.question}</span>
        {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
      </button>
      {isOpen && (
        <div 
          className={styles.answer}
          dangerouslySetInnerHTML={{ __html: item.answer }}
        />
      )}
    </div>
  );
}

export default function FAQPage() {
  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <Link to="/login" className={styles.backLink}>
          <ArrowLeft size={20} />
          <span>Back to Login</span>
        </Link>

        <div className={styles.header}>
          <img src="/peapil-logo.png" alt="Peapil Health" className={styles.logo} />
          <h1 className={styles.title}>Frequently Asked Questions</h1>
          <p className={styles.subtitle}>Find answers to common questions about Peapil Health</p>
        </div>

        <div className={styles.categories}>
          {faqData.map((category) => (
            <section key={category.title} className={styles.category}>
              <h2 className={styles.categoryTitle}>{category.title}</h2>
              <div className={styles.faqList}>
                {category.items.map((item, index) => (
                  <FAQItemComponent key={index} item={item} />
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className={styles.contactSection}>
          <p>Still have questions?</p>
          <a href="mailto:support@peapilhealth.com" className={styles.contactLink}>
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
}
